import type { Address } from '@/types/Address';
import type { FAAKnowledgeTest } from '@/types/Performance';
import { formatDate } from '@/functions/formatDate';
import { formatFAAKnowledgeTestArray } from '@/functions/formatFAAKnowledgeTestArray';
import type { PhoneNumbers } from '@/types/PhoneNumbers';
import type FlightSchool from '@/types/FlightSchool';
import type { AvailableParts } from '@/types/Certificate';

export enum StudentCourseStatus {
    enrolled = 'enrolled',
    graduated = 'graduated',
    dropped = 'dropped',
    locked = 'locked',
}

export enum StudentExamStatus {
    passed = 'passed',
    failed = 'failed',
    nottaken = 'not taken',
}

/**
 * Note: are we going to care about the following?
 * - lockoutDate
 * - unlockDate
 * - element[Name|Uuid]
 */
export type StudentCourseLockout = {
    lockedOut: boolean;

    // used for tooltips in reports. Displayed on badge on Student Record.
    reason: string;
};

export type FlightSchoolStudent = {
    id: number;
    email: string;
    firstName: string;
    lastName: string;
    phoneNumbers: PhoneNumbers;
    address: Address;
};

export type FlightSchoolCourseStudent = FlightSchoolStudent & {
    enrollDate: Date;
    ratingUuid: string;
    lastAccess: Date | null;
    courseStatus: StudentCourseStatus;
    examStatus: StudentExamStatus;
    gradDate: Date | null;
    examDate: Date | null;
    faaTestGrades: FAAKnowledgeTest[];
    lockout: StudentCourseLockout | null;
    flightSchool: FlightSchool;
    courseAcronym: string;
    courseName: string;
    ratingAcronym: string;
    part: AvailableParts;
};

export type PrintableCourseStudent = FlightSchoolCourseStudent & {
    name: `${FlightSchoolCourseStudent['firstName']} ${FlightSchoolCourseStudent['lastName']}`;
    enrollDateString: string;
    lastAccessString: string;
    gradDateString: string;
    examDateString: string;
    faaTestGradesString: string;
    schoolName: string;
};

export function printableStudentFromCourseStudent(
    student: FlightSchoolCourseStudent,
): PrintableCourseStudent {
    return {
        ...student,
        name: `${student.firstName} ${student.lastName}`,
        enrollDateString: formatDate(student.enrollDate),
        lastAccessString: formatDate(student.lastAccess),
        examDateString: formatDate(student.examDate),
        gradDateString: formatDate(student.gradDate),
        faaTestGradesString: formatFAAKnowledgeTestArray(student.faaTestGrades),
        schoolName: student.flightSchool.name,
    };
}
